/**
 * The tab side of the shared repo (R8 file 3;
 * design/mod-core-from-scratch.md). Spawns the shared-repo SharedWorker
 * (shared-repo.worker.ts), donates a MessagePort, and returns a THIN
 * tab Repo whose only peer is the shared repo — components then use STOCK
 * automerge-repo-react-hooks over it (provider wiring is file 4).
 *
 * NO FLAG (pre-launch principle: replace + delete, no flag ceremony — flags
 * rot; `isBrowserSubductionTransportEnabled === false` with subduction still
 * loaded was this codebase's own cautionary tale). Sequencing is by wiring:
 * this module is dead code until the composition slice mounts it
 * unconditionally; during surface migration the legacy engine simply
 * COEXISTS (different IndexedDB, different server path — no conflict), then
 * gets deleted surface by surface.
 *
 * Preconditions & choices:
 * - WASM: while the legacy engine coexists, the app boot has already
 *   initialized both wasm modules by the time anything calls this (main.tsx
 *   runs subduction initSync; the legacy fullfat imports auto-init automerge
 *   core on the main thread). This module therefore does NOT init wasm; when
 *   the legacy engine is deleted, the explicit slim init moves into the app
 *   boot alongside the flip.
 * - A browser tab's Repo is EPHEMERAL (no storage): the shared repo owns
 *   persistence. A direct desktop tab keeps a small store of its own (3e).
 *   (mod-shared-repo-v1). Sharing tabs also persist native documents locally
 *   so account setup can checkpoint its World before an asynchronous worker
 *   sync is acknowledged. Patchwork gives its tabs IndexedDB too — that is a
 *   measured optimization to revisit, not a starting assumption.
 * - Relay: derived from VITE_SYNC_SERVER_URL + the server's /subduction
 *   acceptor path; `localStorage.MOD_WORKER_SYNC_RELAY` overrides for dev
 *   (e.g. ws://localhost:3941/subduction against the R2 local server).
 */

import type {
  AnyDocumentId,
  PeerId,
  Repo,
} from '@automerge/automerge-repo/slim'
import { MessageChannelNetworkAdapter } from '@automerge/automerge-repo-network-messagechannel'
import { WebSocketClientAdapter } from '@automerge/automerge-repo-network-websocket'
import { IndexedDBStorageAdapter } from '@automerge/automerge-repo-storage-indexeddb'
import {
  createModRepo,
  HELD_REPORT_INTERVAL_MS,
  TAB_PEER_PREFIX,
} from '@mod/core/core'
import {
  type ProjectDoc,
  registerPrivateSessionCatalog,
  registerPrivateSessionCreator,
  requirePrivateSessionCatalog,
  type SessionDoc,
  type WorldDoc,
} from '@mod/core/model'
import { registerSessionPoster } from '@mod/core/run'
import { noteRealm, startRealmReport } from '../../memory/realmReport'
import { setDesktopInvite } from '../../sharing/desktopShare'
import { WORLD_KEY } from '../world'
import { desktopDirectRequested } from './desktop-direct'
import { noteDocumentReady } from './document-ready'
import { askBackOnFind, createHeldDocs, type HeldDocs } from './held-docs'
import {
  forwardLocalSync,
  LOCAL_SYNC_EVENT,
  LOCAL_SYNC_GLOBAL,
  localSyncUrl,
} from './local-sync'
import { DESKTOP_TAB_DB_NAME, SHARED_REPO_DB_NAME } from './repo-database'

/** Where the tab keeps the moves its repo has learned. */
const MOVES_KEY = 'mod.moves'
const DESKTOP_MIGRATION_KEY = 'mod.desktop.repo-owner.v1'

/**
 * The relay the shared repo tunnels subduction to, or `null` for LOCAL-ONLY
 * (storage-only worker: IndexedDB, no subduction, no ticket).
 *
 * Priority:
 *   1. `localStorage.MOD_WORKER_SYNC_RELAY` (dev escape hatch):
 *      `off`/`local`/`none` → null (local-only); any other value → that URL.
 *   2. an explicit `relay` (a surface pointing at a SPECIFIC server, e.g. the
 *      new-stack surface at the subduction server — the legacy `VITE_SYNC_SERVER_URL`
 *      is the classic-WS server and can't be reused for it).
 *   3. `defaultLocalOnly` → null (a self-contained surface like `/local` works
 *      with zero setup / in incognito).
 *   4. `VITE_SYNC_SERVER_URL` + `/subduction` (the legacy default).
 */
const LOCAL_ONLY_SENTINELS = new Set(['off', 'local', 'none'])

function resolveRelayUrl(opts: {
  relay?: string
  defaultLocalOnly?: boolean
}): string | null {
  try {
    const override = localStorage.getItem('MOD_WORKER_SYNC_RELAY')
    if (override) {
      return LOCAL_ONLY_SENTINELS.has(override.trim().toLowerCase())
        ? null
        : override
    }
  } catch {
    // no localStorage (SSR/tests) — fall through
  }
  if (opts.relay) return opts.relay
  if (opts.defaultLocalOnly) return null
  const base = import.meta.env.VITE_SYNC_SERVER_URL as string | undefined
  return base ? `${base.replace(/\/$/, '')}/subduction` : null
}

export interface TabRepoBoot {
  /** The thin tab Repo — mount it on RepoContext; its only peer is the shared repo. */
  repo: Repo
  sharing?: Awaited<
    ReturnType<
      typeof import('../../sharing/browser-repo').openBrowserSharingRepo
    >
  > & { requestDocument(documentId: string): void }
  /** Shared-repo identity; a CHANGED id after a reconnect means it
   *  restarted (fresh handles) — patchwork's restart-detection signal. */
  instanceId: string
  /** The relay the shared repo tunnels subduction to (null = local-only). */
  relay: string | null
  /** RFC 0317: what this tab holds; a heavy document is let go of when its
   *  last holder unmounts. */
  held: HeldDocs
}

let bootPromise: Promise<TabRepoBoot> | null = null

/**
 * Boot (once per tab): spawn/attach the shared repo,
 * donate a repo port, return the thin tab Repo + shared-repo controls.
 */
export function createTabRepo(opts?: {
  relay?: string
  defaultLocalOnly?: boolean
}): Promise<TabRepoBoot> {
  // Per-tab singleton: the FIRST caller's options win (the relay is baked into
  // the shared-worker URL). A dedicated surface (`/local`, a new-stack project)
  // is its own tab load.
  // RFC 0345 3a: on a Mac the daemon is the device. It signed in and holds the
  // keys, so the desktop window is its peer over loopback, not a second device
  // with a sign-in of its own. A browser tab is its own keyed device.
  bootPromise ??= desktopDirectRequested()
    ? bootDirectTabRepoWithRetry(opts ?? {})
    : bootTabRepo(opts ?? {})
  return bootPromise
}

/** How many times a direct boot is tried before the shared worker takes over. */
const DIRECT_BOOT_TRIES = 6
const DIRECT_BOOT_RETRY_MS = 10_000

/**
 * A direct boot that fails because the daemon is not up yet is TRIED AGAIN,
 * not handed to the shared worker. After a reboot on 2026-10-22 the window
 * came up a minute before the daemon, the one failed try fell back to the
 * shared worker, and the fallback cleared the cutover marker (see
 * `bootTabRepo`), so every later load walked the whole 675 MB shared store
 * into the daemon again and sat on "Connecting…" for minutes. The daemon
 * answers within a minute of launch; waiting for it costs seconds, the
 * fallback costs every load after.
 *
 * The old IndexedDB store is untouched by migration, so when every try fails
 * the durable shared-worker path still serves the window rather than
 * stranding it at boot.
 */
async function bootDirectTabRepoWithRetry(opts: {
  relay?: string
  defaultLocalOnly?: boolean
}): Promise<TabRepoBoot> {
  let lastError: unknown
  for (let attempt = 1; attempt <= DIRECT_BOOT_TRIES; attempt++) {
    try {
      return await bootDirectTabRepo(opts)
    } catch (error) {
      lastError = error
      console.error(
        `[mod repo] direct desktop boot failed (try ${attempt} of ${DIRECT_BOOT_TRIES}):`,
        error,
      )
      noteRealm(
        'desktop repo',
        `direct boot failed, trying again (${attempt} of ${DIRECT_BOOT_TRIES})`,
      )
      await new Promise((resolve) => setTimeout(resolve, DIRECT_BOOT_RETRY_MS))
    }
  }
  console.error('[mod repo] direct desktop boot gave up:', lastError)
  noteRealm('desktop repo', 'using shared repo after direct boot failed')
  return bootTabRepo(opts)
}

export { desktopDirectRequested }

/**
 * RFC 0345 3a: a sharing World keeps its sessions in the person's private
 * catalog, on the World. The daemon bound it when it opened the World; the
 * window binds the same World, read through the daemon, so its session
 * writes land there and never in a protected project's registry. The
 * daemon's Keyhive policy still decides what it signs and sends.
 */
async function bindDaemonCatalog(repo: Repo): Promise<void> {
  requirePrivateSessionCatalog(repo)
  // The Mac's shell seeds the enrolled account's World into this key before
  // the window loads. readWorldPointer would ask the very catalog bound
  // below, so on a sharing build it is always empty here.
  const worldId = (() => {
    try {
      return localStorage.getItem(WORLD_KEY)?.replace(/^automerge:/, '')
    } catch {
      return undefined
    }
  })()
  if (!worldId) {
    console.error('[sharing] no World from the Mac: sessions cannot be made')
    return
  }
  try {
    const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
    await world.whenReady()
    registerPrivateSessionCatalog(repo, world, async (projectId) => {
      const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
      await project.whenReady()
      if (!project.doc()?.sharingGroupId)
        throw new Error('Destination is not a sharing project')
    })
  } catch (error) {
    console.error('[sharing] the daemon World did not open', error)
  }
}

/** How long a direct tab waits for the daemon to turn up as a peer. */
const DAEMON_PEER_WAIT_MS = 120_000

function waitForDaemonOffer(): Promise<string> {
  const current = localSyncUrl(
    (globalThis as Record<string, unknown>)[LOCAL_SYNC_GLOBAL],
  )
  if (current) return Promise.resolve(current)
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      window.removeEventListener(LOCAL_SYNC_EVENT, onOffer)
      reject(new Error('the desktop daemon did not offer local sync'))
    }, 15_000)
    const onOffer = (event: Event) => {
      const url = localSyncUrl((event as CustomEvent).detail)
      if (!url) return
      clearTimeout(timer)
      window.removeEventListener(LOCAL_SYNC_EVENT, onOffer)
      resolve(url)
    }
    window.addEventListener(LOCAL_SYNC_EVENT, onOffer)
  })
}

/** Keep heartbeats independent of document fetches, but order release/reopen. */
export function desktopControl(
  endpoint: string,
  peerId: string,
  controlToken: string,
  request: typeof fetch = fetch,
): (body: Record<string, unknown>) => Promise<unknown> {
  let orderedTail: Promise<void> = Promise.resolve()
  let heldTail: Promise<void> = Promise.resolve()
  const send = async (body: Record<string, unknown>): Promise<unknown> => {
    const response = await request(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, peerId, controlToken }),
      // Large histories can take well over 20 seconds to reopen. The daemon
      // gives wanted 120 seconds; the client leaves room for its response.
      signal: AbortSignal.timeout(
        // Minting an invite or a protected session waits on Keyhive.
        ['wanted', 'invite', 'create-session'].includes(String(body.type))
          ? 130_000
          : 20_000,
      ),
    })
    if (!response.ok) {
      // The daemon answers an error with its text; show that, not the code.
      const body = (await response.json().catch(() => null)) as {
        error?: unknown
      } | null
      throw new Error(
        typeof body?.error === 'string'
          ? body.error
          : `daemon control: ${response.status}`,
      )
    }
    return response.json()
  }
  return (body) => {
    // A slow wanted/bootstrapping read must not starve the held heartbeat.
    // Keep release-before-wanted ordering within the document-control lane.
    const heldReport = body.type === 'held'
    const work = (heldReport ? heldTail : orderedTail).then(() => send(body))
    const settled = work.then(
      () => undefined,
      () => undefined,
    )
    if (heldReport) heldTail = settled
    else orderedTail = settled
    return work
  }
}

/**
 * The direct desktop tab's own store (RFC 0359 3e).
 *
 * The daemon is the tab's persistent peer, but a write made while the daemon
 * is down has nowhere to go: the tab repo was storage-free, so that write
 * lived only in this tab's memory and a reload lost it. The October 19
 * loopback probe reproduced exactly that.
 *
 * So the direct tab keeps a store of its own. It holds what the tab has
 * opened, ON DISK rather than in a second wasm heap, so it costs none of the
 * memory the shared worker did; a write the tab makes alone is there on the
 * next boot and syncs to the daemon once it is back. Eviction is
 * `removeFromCache`, never deletion, so letting a document go does not touch
 * this store.
 */
export function desktopTabStorage(): IndexedDBStorageAdapter {
  return new IndexedDBStorageAdapter(DESKTOP_TAB_DB_NAME)
}

/** The tab keeps its UI handles; the daemon owns persistence and relay sync. */
async function bootDirectTabRepo(opts: {
  relay?: string
  defaultLocalOnly?: boolean
}): Promise<TabRepoBoot> {
  const url = await waitForDaemonOffer()
  await migrateDesktopStore(url)
  const peerId = `${TAB_PEER_PREFIX}${crypto.randomUUID()}` as PeerId
  const controlToken = crypto.randomUUID()
  const repo = createModRepo({
    moveMemory: {
      read: () => JSON.parse(localStorage.getItem(MOVES_KEY) ?? '{}'),
      write: (moves) => localStorage.setItem(MOVES_KEY, JSON.stringify(moves)),
    },
    network: [
      new WebSocketClientAdapter(
        `${url}${url.includes('?') ? '&' : '?'}controlToken=${controlToken}`,
        5_000,
      ),
    ],
    peerId,
    storage: desktopTabStorage(),
  })
  noteRealm('desktop repo', 'waiting for daemon peer')
  const endpoint = `${url.replace(/^ws:/, 'http:').replace(/\/sync$/, '')}/repo/control`
  const control = desktopControl(endpoint, peerId, controlToken)
  // RFC 0345 3a: the daemon is this Mac's signed-in device and the window has
  // no signer, so a post to a protected session is signed by the daemon.
  if (import.meta.env.VITE_MOD_KEYHIVE === '1') {
    registerSessionPoster(repo, async (sessionId, text, options) => {
      await control({ type: 'post', sessionId, text, options })
      return ''
    })
    // Nor can it make a protected session: the daemon makes it, owned by
    // this Mac's person and listed in their World, and the window opens it.
    // And it cannot invite: the daemon sends a session's invites (3h).
    setDesktopInvite(
      async (input) => {
        const sent = (await control({ type: 'invite', ...input })) as {
          link?: unknown
        }
        if (typeof sent?.link !== 'string')
          throw new Error('The daemon did not send the invite')
        return { link: sent.link }
      },
      async (sessionId) => {
        const found = (await control({ type: 'members', sessionId })) as {
          rows?: unknown
        }
        return Array.isArray(found?.rows) ? found.rows : []
      },
    )
    registerPrivateSessionCreator(repo, async (projectId, options) => {
      const made = (await control({
        type: 'create-session',
        projectId,
        options: options ?? {},
      })) as { sessionId?: unknown }
      if (typeof made?.sessionId !== 'string')
        throw new Error('The daemon did not make the session')
      const handle = await repo.find<SessionDoc>(
        made.sessionId as AnyDocumentId,
      )
      await handle.whenReady()
      return handle
    })
  }
  // AS PATIENT AS THE STORE WAIT ABOVE. A freshly launched daemon spends its
  // first minutes hydrating with its event loop blocked, and the page boots
  // within seconds of it. Fifteen seconds here was shorter than that stall,
  // so the direct boot timed out, fell back to the shared worker, and the
  // window silently paid for two repos again. `migrateDesktopStore` already
  // waits two minutes for the same daemon's store; the peer gets the same.
  const deadline = Date.now() + DAEMON_PEER_WAIT_MS
  let lastNote = Date.now()
  while (repo.peers.length === 0) {
    if (Date.now() > deadline) {
      noteRealm('desktop repo', 'daemon peer timed out')
      await repo.shutdown()
      throw new Error('desktop tab did not connect to daemon')
    }
    if (Date.now() - lastNote >= 10_000) {
      lastNote = Date.now()
      noteRealm(
        'desktop repo',
        `still waiting for daemon peer (${Math.round((deadline - Date.now()) / 1000)}s left)`,
      )
    }
    await new Promise((resolve) => setTimeout(resolve, 25))
  }
  noteRealm('desktop repo', 'daemon peer connected')
  if (import.meta.env.VITE_MOD_KEYHIVE === '1') await bindDaemonCatalog(repo)
  startRealmReport('tab', repo)
  const markEvicted = askBackOnFind(repo, (id) => {
    void control({ type: 'wanted', id }).catch(console.error)
  })
  const held = createHeldDocs(repo, {
    onEvict: (ids) => {
      markEvicted(ids)
      void control({ type: 'released', ids }).catch(console.error)
      reportHeld()
    },
  })
  const reportHeld = (): void => {
    void control({ type: 'held', ids: held.loaded() }).catch(console.error)
  }
  reportHeld()
  setInterval(
    () => void held.sweep().finally(reportHeld),
    HELD_REPORT_INTERVAL_MS,
  )
  return {
    repo,
    instanceId: `daemon-${peerId}`,
    relay: resolveRelayUrl(opts),
    held,
  }
}

/** Opt-in trial: verify the old browser store before this tab leaves it behind. */
export async function migrateDesktopStore(url: string): Promise<void> {
  if (!navigator.locks)
    throw new Error('desktop migration requires cross-window Web Locks')
  // Multiple desktop windows may launch together. Only the first transfers;
  // each later window checks the marker after acquiring the same lock.
  await navigator.locks.request('mod-desktop-repo-owner', async () => {
    const endpoint = `${url.replace(/^ws:/, 'http:').replace(/\/sync$/, '')}/repo/store-id`
    // The shell can publish the loopback socket while the daemon is still
    // opening its repo. Wait for that round rather than immediately choosing
    // the shared worker on every cold start of a large store.
    const deadline = Date.now() + 120_000
    let response: Response | undefined
    for (;;) {
      try {
        // A BODY, EVEN AN EMPTY ONE. The daemon parses every POST as JSON and
        // a request with no body parsed as "Unexpected end of JSON input": a
        // 500, on the first request of every direct boot, so the direct path
        // never engaged and the window silently kept two repos. `desktopControl`
        // sends JSON; this now asks the same way.
        response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: '{}',
          signal: AbortSignal.timeout(10_000),
        })
      } catch {
        response = undefined
      }
      // Any 5xx while the daemon is coming up is "not yet", not "never": the
      // deadline below bounds the wait, and only a definite non-5xx answer or
      // the deadline ends it.
      if (response && response.status < 500) break
      if (Date.now() >= deadline)
        throw new Error('daemon store did not become ready')
      noteRealm('desktop repo', 'waiting for daemon store')
      await new Promise((resolve) => setTimeout(resolve, 500))
    }
    if (!response.ok)
      throw new Error(`daemon store identity unavailable: ${response.status}`)
    const store = (await response.json()) as { id?: unknown }
    if (typeof store.id !== 'string' || !store.id)
      throw new Error('daemon store identity invalid')
    const marker = `v1:${store.id}`
    if (localStorage.getItem(DESKTOP_MIGRATION_KEY) === marker) return

    let databases: IDBDatabaseInfo[] | undefined
    try {
      databases = await indexedDB.databases?.()
    } catch {
      // Older/private browser contexts may not permit database enumeration. The
      // worker can still open the store and perform an empty pass if it is new.
    }
    if (databases && !databases.some((db) => db.name === SHARED_REPO_DB_NAME)) {
      localStorage.setItem(DESKTOP_MIGRATION_KEY, marker)
      return
    }
    noteRealm('desktop repo', 'checking stored documents on daemon')
    const worker = new Worker(
      new URL('./desktop-migrate.worker.ts', import.meta.url),
      { type: 'module' },
    )
    try {
      await new Promise<void>((resolve, reject) => {
        worker.onmessage = (event: MessageEvent) => {
          if (event.data?.type === 'progress')
            noteRealm('desktop repo', `migrated ${event.data.done} documents`)
          if (event.data?.type === 'done') resolve()
          if (event.data?.type === 'error') reject(new Error(event.data.error))
        }
        worker.onerror = (event) => reject(new Error(event.message))
        worker.postMessage({ type: 'migrate', url })
      })
      // The daemon has flushed each acknowledged head before the worker says
      // done. Keep the old database intact for rollback and recovery.
      localStorage.setItem(DESKTOP_MIGRATION_KEY, marker)
    } finally {
      worker.terminate()
    }
  })
}

async function bootTabRepo(opts: {
  relay?: string
  defaultLocalOnly?: boolean
}): Promise<TabRepoBoot> {
  // A rollback to the shared-worker path can add new browser-only writes.
  // The next direct launch must hand them to the daemon again.
  if (/Electron/.test(navigator.userAgent)) {
    try {
      localStorage.removeItem(DESKTOP_MIGRATION_KEY)
    } catch {
      // The shared worker remains usable even if this browser rejects storage.
    }
  }
  const relay = resolveRelayUrl(opts)

  // The worker construction differs between dev and build because Vite handles the
  // two cases oppositely:
  //  - BUILD: `new SharedWorker(new URL('./x.ts', import.meta.url), …)` must be
  //    LITERAL so Vite compiles the worker to one .js chunk. A variable / mutated
  //    URL ships the raw .ts, which the browser can't execute (prod boot hangs).
  //    The relay rides the worker NAME (read as `self.name`), keeping the URL literal.
  //  - DEV: that literal form makes Vite serve the worker as a SEPARATE cold module
  //    bundle (re-fetching the whole mod-core/automerge/subduction graph in the
  //    worker context) — unusably slow / appears stuck. The old plain-module URL,
  //    which dev serves directly (reusing the warm graph), is fast. So dev builds
  //    the URL and puts the relay in its query (read as `self.location.search`).
  // The worker reads the relay from BOTH sources, so either path works.
  let sharedRepoWorker: SharedWorker
  if (import.meta.env.DEV) {
    const devWorkerUrl = new URL('./shared-repo.worker.ts', import.meta.url)
    if (relay) devWorkerUrl.searchParams.set('relay', relay)
    sharedRepoWorker = new SharedWorker(devWorkerUrl, {
      type: 'module',
      name:
        import.meta.env.VITE_MOD_KEYHIVE === '1'
          ? 'mod-sharing-repo-v1'
          : 'mod-shared-repo',
    })
  } else {
    sharedRepoWorker = new SharedWorker(
      new URL('./shared-repo.worker.ts', import.meta.url),
      {
        type: 'module',
        name: `${import.meta.env.VITE_MOD_KEYHIVE === '1' ? 'mod-sharing-repo-v1' : 'mod-shared-repo'}${relay ? `?relay=${encodeURIComponent(relay)}` : ''}`,
      },
    )
  }

  const channel = new MessageChannel()
  const controlPort = sharedRepoWorker.port

  const ready = new Promise<{ instanceId: string; relay: string | null }>(
    (resolve, reject) => {
      controlPort.onmessage = (event: MessageEvent) => {
        const { type } = event.data ?? {}
        if (type === 'boot-error') reject(new Error(String(event.data.message)))
        if (type === 'ready') {
          resolve({
            instanceId: event.data.instanceId,
            relay: event.data.relay ?? null,
          })
        }
        if (type === 'document-stalled') {
          const d = event.data
          console.warn(
            `[sharing] document not served after 60 s: ${d.documentId} (${d.state}); this device in its CGKA: ${d.member}; access: ${d.access}`,
          )
        }
        if (type === 'document-ready') {
          const id = String(event.data.documentId)
          console.debug(`[sharing] document ready at worker: ${id}`)
          repo.resyncSubduction(id as never)
          noteDocumentReady(id)
        }
      }
    },
  )

  // Donate the repo port + the RFC 0228 bearer (slice 3.5b). The shared repo
  // runs in a SharedWorker with no localStorage, so it cannot read the sync
  // bearer itself; the tab (main thread) hands it over so the worker can mint
  // the mod.ticket the subduction handshake requires. Safe to post immediately:
  // the shared repo installs onconnect before its first await and buffers ports
  // (connect-before-await), and MessagePort queues messages until drained.
  let bearer: string | null = null
  try {
    bearer = localStorage.getItem('mod_auth_token')
  } catch {
    // no localStorage (SSR/tests) — worker connects ticketless (local-only/dev)
  }
  controlPort.postMessage({ type: 'connect', bearer }, [channel.port2])
  const sharedRepoReady = await ready

  // RFC 0310: when the desktop shell says where the daemon's sync socket is, the
  // shared repo connects to it beside the relay. Never unsubscribed: the tab
  // repo lives as long as the page.
  forwardLocalSync((url) =>
    controlPort.postMessage({ type: 'local-sync', url }),
  )

  let sharing: TabRepoBoot['sharing']
  const requestedDocuments = new Set<string>()
  let repo: Repo
  if (import.meta.env.VITE_MOD_KEYHIVE === '1') {
    const [{ AttachedPeersAdapter }, { openBrowserSharingRepo }] =
      await Promise.all([
        import('@mod/core/sharing'),
        import('../../sharing/browser-repo'),
      ])
    const transport = new AttachedPeersAdapter()
    transport.attach(new MessageChannelNetworkAdapter(channel.port1))
    // OAuth navigation can put the old page in the back-forward cache. Its
    // native connection still has this device's signing identity, so a worker
    // fetch can otherwise land on that frozen repo instead of the current tab.
    window.addEventListener('pagehide', () => transport.disconnect(), {
      once: true,
    })
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) window.location.reload()
    })
    const opened = await openBrowserSharingRepo({
      peerLabel: 'tab',
      repo: {
        subductionAdapters: [
          {
            adapter: transport,
            serviceName: 'mod-sharing-tabs-v1',
            role: 'connect',
          },
        ],
      },
    })
    sharing = {
      ...opened,
      // Native Subduction serves the worker's local trees. A newly granted
      // document needs an upstream query before the worker can serve the tab.
      requestDocument: (documentId) => {
        const id = documentId.replace(/^automerge:/, '')
        if (requestedDocuments.has(id)) return
        requestedDocuments.add(id)
        controlPort.postMessage({ type: 'find-document', documentId: id })
        setTimeout(() => requestedDocuments.delete(id), 60_000)
      },
    }
    repo = sharing.repo
  } else {
    repo = createModRepo({
      // Known moves survive a reload, so a session naming the old project never
      // loads its 3 million ops before the World is read (RFC 0346).
      moveMemory: {
        read: () => JSON.parse(localStorage.getItem(MOVES_KEY) ?? '{}'),
        write: (moves) =>
          localStorage.setItem(MOVES_KEY, JSON.stringify(moves)),
      },
      network: [new MessageChannelNetworkAdapter(channel.port1)],
      // The worker sends a tab only what it asks for (RFC 0317 3d).
      peerId: `${TAB_PEER_PREFIX}${crypto.randomUUID()}` as PeerId,
    })
  }

  /**
   * RFC 0317 §3b: TELL THE WORKER WHAT THIS TAB HOLDS.
   *
   * The shared repo evicts what no tab reports. It cannot tell on its own: a
   * tab that opened a document once and is still showing it makes no further
   * requests. So the tab repo's handle set goes over on an interval, and once
   * more when a handle is let go of, so the worker's picture is never older
   * than one report. Never unsubscribed: the tab repo lives as long as the page.
   */
  // RFC 0317 3d: an evicted document comes back only when asked for.
  const markEvicted = askBackOnFind(repo, (id) =>
    controlPort.postMessage({ type: 'wanted', id, peerId: repo.peerId }),
  )
  const held = createHeldDocs(repo, {
    onEvict: (ids) => {
      markEvicted(ids)
      controlPort.postMessage({ type: 'released', ids, peerId: repo.peerId })
      reportHeld()
    },
  })
  const reportHeld = (): void =>
    controlPort.postMessage({ type: 'held', ids: held.loaded() })
  // RFC 0317 §3d: what nothing holds leaves on the same beat. An eviction
  // reports on its own, so this report covers the quiet ticks.
  setInterval(() => {
    void held.sweep().finally(reportHeld)
  }, HELD_REPORT_INTERVAL_MS)

  // What this tab holds, every 30 seconds, to the daemon (RFC 0346 S2).
  startRealmReport('tab', repo)

  return {
    repo,
    ...(sharing ? { sharing } : {}),
    instanceId: sharedRepoReady.instanceId,
    relay: sharedRepoReady.relay,
    held,
  }
}
